import { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { shotsApi } from "../app/services/shots";
import toast from "react-hot-toast";

const FADE_OUT_DURATION = 700;

interface UseAnimatedStatusChangeOptions {
  animationDuration?: number;
  onComplete?: () => void;
}

interface ExecuteOptions {
  /** フェードアウト開始前の待機時間（例: マイクロインタラクション 1500ms） */
  delay?: number;
}

interface UseAnimatedStatusChangeReturn {
  fadingOut: boolean;
  executeWithAnimation: (
    apiCall: () => Promise<unknown>,
    options?: ExecuteOptions
  ) => void;
}

/**
 * ステータス変更時に fade-out-bck アニメーションと API 呼び出しを協調させる。
 *
 * updateShotStatusAnimated（invalidatesTags なし）と組み合わせて使う。
 * アニメーション完了後に onComplete（= refetchShots）を呼ぶことで、
 * アニメーション中にリストが再描画されるのを防ぐ。
 *
 * API エラー時はフェードアウトをリセットし、Shots タグを手動で無効化して
 * リストを元に戻す。
 */
export const useAnimatedStatusChange = ({
  animationDuration = FADE_OUT_DURATION,
  onComplete,
}: UseAnimatedStatusChangeOptions = {}): UseAnimatedStatusChangeReturn => {
  const [fadingOut, setFadingOut] = useState(false);
  const isRunning = useRef(false);
  const dispatch = useDispatch();

  const executeWithAnimation = useCallback(
    (apiCall: () => Promise<unknown>, options?: ExecuteOptions) => {
      if (isRunning.current) return;
      isRunning.current = true;

      const { delay } = options ?? {};

      const run = async () => {
        try {
          const apiPromise = apiCall();

          if (delay && delay > 0) {
            await Promise.all([
              apiPromise,
              new Promise<void>((r) => setTimeout(r, delay)),
            ]);
            setFadingOut(true);
            await new Promise<void>((r) => setTimeout(r, animationDuration));
          } else {
            setFadingOut(true);
            await Promise.all([
              apiPromise,
              new Promise<void>((r) => setTimeout(r, animationDuration)),
            ]);
          }

          // fadingOut は true のまま維持。refetch でカードが DOM から
          // 消えるまで非表示を保つ（コンポーネント破棄で自然にクリーンアップ）
          isRunning.current = false;
          onComplete?.();
        } catch {
          isRunning.current = false;
          setFadingOut(false);
          toast.error("ステータスの更新に失敗しました");
          dispatch(shotsApi.util.invalidateTags(["Shots"]));
        }
      };

      run();
    },
    [animationDuration, onComplete, dispatch]
  );

  return { fadingOut, executeWithAnimation };
};

export default useAnimatedStatusChange;
